import { SelectOption } from '../../../../../components/SelectDropdown/SelectDropdown'
import React, { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Col, Form, Row, Spinner } from 'react-bootstrap'
import { FormFeedback } from '../../../../../components/Form/FormFeedback'
import { FormTask } from '../AddTaskToCRMModal'
import { Button } from '../../../../../components/Button/Button'
import { ResolvedIdentity, Task } from '../../../../../models/MeetingSupport'
import { CreateTaskPayload, Identity } from '../../../../../store/meetingSupport/meetingSupport'
import { addToast } from '../../../../../store/toast/toastSlice'
import { TaskSourceType, useTaskMutations } from '../../../../Tasks/hooks'
import { selectSafeAuthenticatedUser } from '../../../../../store/auth/authSlice'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { TrackEvent, useTracking } from '../../../../../apps/professional-app/fa/common/tracking'
import { useMergeTasks } from './hooks'

export type EditMode = 'add' | 'merge' | 'edit'

export interface TaskContextDetails {
  roomId?: string
  sessionId?: string
  emailId?: string
  threadId?: string
  emailConnector?: string
  userId?: string
  contactId?: string
}

interface TaskEditSectionProps {
  mode: EditMode
  onEditSaved: () => void
  onEditCancelled: () => void
  taskToEdit?: Task | null
  tasksToMerge: FormTask[]
  assignedToOptions: SelectOption[]
  taskCategoryOptions: SelectOption[]
  type: TaskSourceType
  taskContextDetails: TaskContextDetails
}

export type EditTaskFormValues = {
  newTaskName: string
  newTaskDescription?: string
}

export const TaskEditSection: React.FC<TaskEditSectionProps> = ({
  mode,
  onEditSaved,
  onEditCancelled,
  taskToEdit,
  tasksToMerge,
  assignedToOptions,
  taskCategoryOptions,
  type,
  taskContextDetails,
}) => {
  const { tasks, createTasks, isCreatingTasks, updateTaskById, isUpdatingTask, deleteTask, isDeletingTask } = useTaskMutations()
  const isSaving = isCreatingTasks || isUpdatingTask || isDeletingTask
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const dispatch = useAppDispatch()
  const taskMerger = useMergeTasks()
  const [isMerging, setIsMerging] = useState(false)

  const defaultValues: EditTaskFormValues = {
    newTaskName: taskToEdit ? taskToEdit.task : '',
    newTaskDescription: taskToEdit ? taskToEdit.description : '',
  }
  const { control, handleSubmit, setValue } = useForm<EditTaskFormValues>({ defaultValues })

  useEffect(() => {
    if (mode !== 'merge' || tasksToMerge.length === 0) {
      return
    }
    const mergeTasks = async () => {
      setIsMerging(true)
      try {
        const result = await taskMerger(tasksToMerge)
        if (result) {
          setValue('newTaskName', result.subject)
          setValue('newTaskDescription', result.description)
        }
      } catch (error) {
        console.error('Failed to merge tasks:', error)
        dispatch(
          addToast({
            variant: 'error',
            messages: ['Failed to merge tasks. Please try again.'],
          }),
        )
      } finally {
        setIsMerging(false)
      }
    }

    mergeTasks()
  }, [mode, tasksToMerge, taskMerger, setValue, dispatch])

  const getAssigneeName = (id: string | string[] | null) => {
    if (Array.isArray(id)) {
      return id?.length
        ? assignedToOptions
            .filter((o) => id.includes(o.id))
            .map((o) => o.label)
            .join(', ')
        : 'N/A'
    } else if (typeof id === 'string') {
      return assignedToOptions.find((o) => o.id === id)?.label || 'N/A'
    } else {
      return 'N/A'
    }
  }
  const getCategoryName = (id: string | null) => (id ? taskCategoryOptions.find((o) => o.id === id)?.label || 'N/A' : 'N/A')

  const getTitle = () => {
    switch (mode) {
      case 'edit':
        return 'Edit Task'
      case 'merge':
        return 'New Task'
      case 'add':
        return 'Add New Task'
      default:
        return ''
    }
  }

  const buildCreateTaskPayload = (taskName: string, taskDescription: string, assignedTo: ResolvedIdentity): CreateTaskPayload => ({
    task: taskName,
    description: taskDescription,
    assignedTo: { id: assignedTo.id, type: assignedTo.type },
    type: type,
    roomId: taskContextDetails.roomId,
    sessionId: taskContextDetails.sessionId,
    emailId: taskContextDetails.emailId,
    threadId: taskContextDetails.threadId,
    emailConnector: taskContextDetails.emailConnector,
  })
  const { tracked } = useTracking()

  const _handleSaveCreateTask = async (formValues: EditTaskFormValues) => {
    const newTaskAssignedTo: Identity = { type: 'user', id: authenticatedUser.id }
    const payload = buildCreateTaskPayload(formValues.newTaskName || 'New Task', formValues.newTaskDescription || '', newTaskAssignedTo)
    await createTasks([payload])
  }

  const handleSaveCreateTask = tracked(_handleSaveCreateTask, {
    eventName: TrackEvent.TASK_CREATED,
  })

  const _handleSaveMergedTasks = async (formValues: EditTaskFormValues) => {
    const deletePromises = tasks.filter((t) => tasksToMerge.some((tt) => tt.id === t.id)).map((task) => deleteTask(task))

    await Promise.all(deletePromises)
    const newTaskAssignedTo: Identity = { type: 'user', id: authenticatedUser.id }
    const payload = buildCreateTaskPayload(formValues.newTaskName || 'New Merged Task', formValues.newTaskDescription || '', newTaskAssignedTo)
    await createTasks([payload])
  }

  const handleSaveMergedTasks = tracked(_handleSaveMergedTasks, {
    eventName: TrackEvent.TASKS_MERGED,
    eventDetails: { tasks: tasksToMerge.map((t) => t.id) },
  })

  const _handleSaveEditTask = async (formValues: EditTaskFormValues) => {
    const existingTask = tasks.find((t) => t.id === taskToEdit?.id)
    if (!existingTask) {
      return
    }
    await updateTaskById(existingTask.id, { task: formValues.newTaskName, description: formValues.newTaskDescription })
  }

  const handleSaveEditTask = tracked(_handleSaveEditTask, {
    eventName: TrackEvent.TASK_UPDATED,
    eventDetails: { task: taskToEdit?.id, type: 'edit' },
  })

  const handleOnSave = async (formValues: EditTaskFormValues) => {
    try {
      if (mode === 'add') {
        await handleSaveCreateTask(formValues)
      } else if (mode === 'merge') {
        await handleSaveMergedTasks(formValues)
      } else if (mode === 'edit' && mode !== null) {
        await handleSaveEditTask(formValues)
      }

      dispatch(
        addToast({
          variant: 'success',
          title: 'Success',
          messages: [`Task successfully ${mode === 'merge' ? 'merged' : mode + 'ed'}.`],
        }),
      )
    } catch (error) {
      console.error(`Failed to ${mode} task:`, error)
      dispatch(
        addToast({
          variant: 'error',
          messages: [`Failed to ${mode} task. Please try again.`],
        }),
      )
    }
    onEditSaved()
  }

  return (
    <Row>
      <Col className={'px-4 py-3'}>
        {mode === 'merge' && (
          <div className={'d-flex flex-column align-items-center pb-3'}>
            <div className='w-100 px-3 pt-3 mb-3 border rounded zocks-border-color-gray-300 align-items-center gap-3'>
              <h5>Tasks to merge</h5>
              <ul className='list-unstyled'>
                {tasksToMerge.map((task) => (
                  <li key={task.id} className='mt-3'>
                    <p className='mb-1'>
                      <strong>{task.task}:</strong> {task.description || 'No description.'}
                    </p>
                    <small className='text-muted d-flex gap-1'>
                      <span>{new Date(task.dueDate).toLocaleDateString()}</span>
                      <span className='text-secondary'>&bull;</span>
                      <span>{getAssigneeName(task.multipleAssignedTo.length ? task.multipleAssignedTo : (task.assignedTo ?? null))}</span>
                      <span className='text-secondary'>&bull;</span>
                      <span>{task.priority}</span>
                      <span className='text-secondary'>&bull;</span>
                      <span>{getCategoryName(task.category)}</span>
                    </small>
                  </li>
                ))}
              </ul>
            </div>
            <i className={'bi bi-arrow-down fs-5'} />
          </div>
        )}

        <div className='zocks-bg-color-gray-100 p-3 border rounded zocks-border-color-gray-300'>
          <h5 className='mb-3'>{getTitle()}</h5>
          <Form.Group controlId='newTaskName'>
            <Form.Label className={'text-uppercase bold'}>Task name</Form.Label>
            <Controller
              name='newTaskName'
              control={control}
              rules={{ required: 'Task name is required' }}
              render={({ field, fieldState }) => (
                <>
                  <div className='position-relative'>
                    <Form.Control type='text' placeholder='Enter task name' {...field} disabled={isSaving || isMerging} />
                    {isMerging && (
                      <div className='position-absolute top-50 end-0 translate-middle-y pe-2'>
                        <Spinner animation='border' size='sm' />
                      </div>
                    )}
                  </div>
                  {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                </>
              )}
            />
          </Form.Group>
          <Form.Group controlId='newTaskDescription' className='mt-3'>
            <Form.Label className={'text-uppercase bold'}>Task description</Form.Label>
            <Controller
              name='newTaskDescription'
              control={control}
              render={({ field }) => (
                <div className='position-relative'>
                  <Form.Control as='textarea' rows={3} placeholder='Enter a brief description if needed' {...field} disabled={isSaving || isMerging} />
                  {isMerging && (
                    <div className='position-absolute top-0 end-0 mt-2 me-2'>
                      <Spinner animation='border' size='sm' />
                    </div>
                  )}
                </div>
              )}
            />
          </Form.Group>
        </div>

        <div className='d-flex justify-content-end mt-3 gap-2'>
          <Button variant='secondary' onClick={onEditCancelled} disabled={isSaving || isMerging}>
            Cancel
          </Button>
          <Button variant='primary' onClick={handleSubmit(handleOnSave)} disabled={isSaving || isMerging} loading={isSaving}>
            {mode === 'merge' ? 'Merge Tasks' : 'Save'}
          </Button>
        </div>
      </Col>
    </Row>
  )
}
